/**
 * LimitationsModal Component
 *
 * Modal displaying important limitations that MUST be shown before viewing results.
 * User must acknowledge limitations by checking a checkbox before proceeding.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  AlertTriangle,
  ClipboardList,
  Users,
  AlertCircle,
  Search,
  FileSearch,
} from 'lucide-react';

interface LimitationsModalProps {
  isOpen: boolean;
  onAcknowledge: () => void;
  onClose: () => void;
}

interface LimitationItem {
  icon: React.ElementType;
  title: string;
  description: string;
}

const LIMITATIONS: LimitationItem[] = [
  {
    icon: ClipboardList,
    title: 'This tool is for PRE-SCREENING ASSISTANCE only',
    description: 'It does not replace clinical judgment or formal eligibility determination',
  },
  {
    icon: Users,
    title: 'All results REQUIRE human review',
    description: 'No patient decisions should be made based solely on AI analysis',
  },
  {
    icon: AlertCircle,
    title: 'AI may miss or misinterpret information',
    description:
      'The system analyzes provided documents but may not capture all relevant details',
  },
  {
    icon: Search,
    title: "'No evidence found' does NOT mean absence",
    description:
      'It only means no evidence was found in the uploaded documents. The condition may exist in records not provided',
  },
  {
    icon: FileSearch,
    title: 'Always verify against source documents',
    description: 'Use this tool as a starting point, not a final determination',
  },
];

export function LimitationsModal({ isOpen, onAcknowledge, onClose }: LimitationsModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const checkboxRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Reset acknowledged state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  // Focus trap for accessibility
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'input[type="checkbox"], button:not([disabled])'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }

      // Prevent Escape key from closing the modal
      if (e.key === 'Escape') {
        e.preventDefault();
      }
    },
    [isOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Focus checkbox when modal opens
  useEffect(() => {
    if (isOpen && checkboxRef.current) {
      setTimeout(() => {
        checkboxRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleAcknowledge = () => {
    if (acknowledged) {
      onAcknowledge();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop - clicking does NOT close the modal */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" aria-hidden="true" />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="limitations-title"
          aria-describedby="limitations-content"
          className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg">
              <AlertTriangle size={24} className="text-amber-600 dark:text-amber-400" />
            </div>
            <h2 id="limitations-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Important Limitations - Please Read Carefully
            </h2>
          </div>

          {/* Content */}
          <div id="limitations-content" className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-4">
              {LIMITATIONS.map((limitation, index) => {
                const Icon = limitation.icon;
                return (
                  <div key={index} className="flex items-start gap-4 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex-shrink-0">
                      <Icon size={20} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{limitation.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{limitation.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            {/* Acknowledgment checkbox */}
            <div className="flex items-start gap-3 mb-4">
              <input
                ref={checkboxRef}
                type="checkbox"
                id="acknowledge-limitations"
                checked={acknowledged}
                onChange={e => setAcknowledged(e.target.checked)}
                className="h-5 w-5 mt-0.5 text-primary-600 dark:text-primary-400 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500 cursor-pointer"
              />
              <label
                htmlFor="acknowledge-limitations"
                className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
              >
                I have read and understand these limitations
              </label>
            </div>

            {/* Continue button */}
            <div className="flex justify-end">
              <button
                ref={buttonRef}
                onClick={handleAcknowledge}
                disabled={!acknowledged}
                className={`px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                  acknowledged
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue to Results
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
